import React from 'react';
import { ArrowLeft, Shield, Lock, FolderOpen } from 'lucide-react';

const PrivacyPolicy = () => {
    return (
        <div className="min-h-screen flex flex-col bg-gray-50">
            {/* Header */}
            <header className="bg-white shadow-sm py-6 sticky top-0 z-10">
                <div className="container mx-auto px-6 flex justify-between items-center">
                    <div className="flex items-center">
                        <FolderOpen className="h-7 w-7 text-teal-600 mr-2" />
                        <a href="/" className="font-semibold text-2xl text-gray-900">
                            ShiftFolder
                        </a>
                    </div>
                    <a href="/" className="text-gray-600 hover:text-teal-600 transition-colors flex items-center">
                        <ArrowLeft size={18} className="mr-2" />
                        Back to Home
                    </a>
                </div>
            </header>

            <div className="container mx-auto px-6 py-12 flex-grow">
                <div className="max-w-4xl mx-auto">
                    <div className="mb-10 flex items-center justify-center">
                        <div className="bg-teal-100 p-4 rounded-full">
                            <Lock size={32} className="text-teal-600" />
                        </div>
                    </div>

                    <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-8 mb-10">
                        <div className="text-center mb-8">
                            <h1 className="text-3xl font-bold text-gray-900 mb-2">Privacy Policy</h1>
                            <p className="text-gray-500">Last updated: March, 2025</p>
                        </div>

                        <div className="prose prose-lg max-w-none">
                            <div className="bg-teal-50 p-6 rounded-lg mb-8">
                                <h2 className="text-xl font-semibold text-teal-800 mb-3">Your Privacy Matters</h2>
                                <p className="text-gray-700">
                                    ShiftFolder is built with privacy at its core. All file operations happen locally on your Mac, and we never access or store your file content. Our file management software functions efficiently on your device, ensuring your sensitive documents remain private.
                                </p>
                            </div>

                            <p className="text-gray-700 mb-6">
                                This Privacy Policy outlines how ShiftFolder handles your information. Our approach is straightforward: we collect minimal data and ensure your files stay on your device.
                            </p>

                            <p className="text-gray-700 mb-6">
                                By using ShiftFolder, you agree to the practices outlined in this Privacy Policy. We encourage you to read it thoroughly to understand our commitment to safeguarding your privacy.
                            </p>

                            <h2 className="text-2xl font-bold mt-10 mb-4 text-gray-800">Information Collection</h2>

                            <h3 className="text-xl font-semibold mt-6 mb-3 text-gray-800">File Data</h3>
                            <p className="text-gray-700 mb-4">
                                <strong>We respect your file privacy.</strong> ShiftFolder processes all files locally on your Mac. We do not:
                            </p>
                            <ul className="list-disc pl-6 mb-6 text-gray-700">
                                <li>Upload your files to any external servers</li>
                                <li>Create or store copies of your files in the cloud</li>
                                <li>Access the content of your processed files</li>
                                <li>Transfer your file data to third parties</li>
                            </ul>

                            <h3 className="text-xl font-semibold mt-6 mb-3 text-gray-800">Usage Statistics</h3>
                            <p className="text-gray-700 mb-4">
                                With your permission, we collect anonymous usage statistics to enhance our software. This data includes:
                            </p>
                            <ul className="list-disc pl-6 mb-6 text-gray-700">
                                <li>Software performance metrics</li>
                                <li>Feature usage patterns (without content details)</li>
                                <li>Error reports for troubleshooting</li>
                                <li>macOS version information</li>
                            </ul>
                            <p className="text-gray-700 mb-4">
                                All collected usage data is anonymized and contains no personally identifiable information or file content. You can disable usage statistics collection in the application settings at any time.
                            </p>

                            <h2 className="text-2xl font-bold mt-10 mb-4 text-gray-800">How We Use Your Information</h2>
                            <p className="text-gray-700 mb-4">
                                The limited data we collect is used exclusively for:
                            </p>
                            <ul className="list-disc pl-6 mb-6 text-gray-700">
                                <li>Enhancing ShiftFolder's functionality and performance</li>
                                <li>Resolving bugs and technical issues</li>
                                <li>Creating new features based on user requirements</li>
                                <li>Understanding how users interact with our application</li>
                            </ul>

                            <h2 className="text-2xl font-bold mt-10 mb-4 text-gray-800">Data Security</h2>
                            <p className="text-gray-700 mb-6">
                                Since ShiftFolder processes your files locally on your Mac, your files are protected by your device's security measures. For the minimal usage data we collect, we implement industry-standard security protocols.
                            </p>

                            <h2 className="text-2xl font-bold mt-10 mb-4 text-gray-800">Third-Party Services</h2>
                            <p className="text-gray-700 mb-6">
                                ShiftFolder does not integrate with third-party services that would access your file content. If you choose to share organized files using your system's sharing capabilities, those actions are governed by the privacy policies of the services you use for sharing.
                            </p>

                            <h2 className="text-2xl font-bold mt-10 mb-4 text-gray-800">Updates to This Policy</h2>
                            <p className="text-gray-700 mb-6">
                                We may update our Privacy Policy periodically. We will notify you of any changes by posting the updated Privacy Policy on our website and updating the "Last updated" date. We recommend reviewing this Privacy Policy occasionally for any changes.
                            </p>

                            <h2 className="text-2xl font-bold mt-10 mb-4 text-gray-800">Children's Privacy</h2>
                            <p className="text-gray-700 mb-6">
                                ShiftFolder is not intended for children under 13. We do not knowingly collect personal information from children under 13. If you are a parent or guardian and believe we may have collected information from your child, please contact us at contact@shiftfolder.com.
                            </p>

                            <h2 className="text-2xl font-bold mt-10 mb-4 text-gray-800">Contact Us</h2>
                            <p className="text-gray-700 mb-4">
                                If you have any questions or concerns about this Privacy Policy, please contact us:
                            </p>
                            <ul className="list-disc pl-6 mb-6 text-gray-700">
                                <li>Email: <a href="mailto:contact@shiftfolder.com" className="text-teal-600 hover:text-teal-800">contact@shiftfolder.com</a></li>
                            </ul>
                        </div>
                    </div>

                    <div className="bg-gray-100 rounded-xl p-6 border border-gray-200">
                        <div className="flex items-start">
                            <div className="mr-4 mt-1">
                                <Shield size={24} className="text-teal-600" />
                            </div>
                            <div>
                                <h3 className="font-semibold text-gray-900 mb-2">We Prioritize Your Privacy</h3>
                                <p className="text-gray-600">
                                    ShiftFolder is dedicated to earning and maintaining your trust. We believe your files should remain private, which is why we've designed our software to keep all data securely on your device.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Footer */}
            <footer className="bg-gray-900 text-gray-400 py-6 mt-12">
                <div className="container mx-auto px-6">
                    <div className="flex justify-center text-sm">
                        <a href="/privacy" className="text-teal-400 font-medium">Privacy Policy</a>
                        <span className="mx-3">|</span>
                        <a href="/terms" className="hover:text-teal-400 transition-colors">Terms of Use</a>
                    </div>
                    <div className="text-center mt-4 text-xs">
                        © {new Date().getFullYear()} ShiftFolder. All rights reserved.
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default PrivacyPolicy;